import { z } from 'zod';

/** Spalten-Metadaten einer editierbaren Tabelle (Daten-Editor). */
export const dataColumnSchema = z.object({
  name: z.string(),
  /** Vereinfachter Typ für die UI-Eingabe. */
  type: z.enum(['text', 'number', 'bigint', 'boolean', 'date', 'uuid', 'json', 'unknown']),
  nullable: z.boolean(),
  /** false = System-/Schema-Spalte (id, tenant_id, created_* …) → nur lesbar. */
  editable: z.boolean(),
});
export type DataColumn = z.infer<typeof dataColumnSchema>;

/** Eine Tabelle der Allowlist + Zeilenzahl. */
export const dataTableInfoSchema = z.object({
  name: z.string(),
  label: z.string(),
  rowCount: z.number(),
});
export type DataTableInfo = z.infer<typeof dataTableInfoSchema>;

/** Werte einer Zeile (JSON-serialisierbar; BigInt als String). */
export const dataRowSchema = z.record(z.string(), z.unknown());
export type DataRow = z.infer<typeof dataRowSchema>;

/** Eine Seite Zeilen inkl. Spalten-Metadaten. */
export const dataRowsResponseSchema = z.object({
  columns: z.array(dataColumnSchema),
  rows: z.array(dataRowSchema),
  total: z.number(),
});
export type DataRowsResponse = z.infer<typeof dataRowsResponseSchema>;

/** Zellwerte zum Schreiben (nur editierbare Spalten; null erlaubt). */
export const dataWriteSchema = z.object({
  values: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export type DataWriteInput = z.infer<typeof dataWriteSchema>;

/**
 * Datenleichen-Finder: ein Prüfergebnis. `category` unterscheidet verwaiste
 * Referenzen (Bezugsobjekt fehlt/gelöscht) von leeren/unvollständigen Sätzen.
 */
export const dataHealthCheckSchema = z.object({
  /** Stabile ID, z. B. `orphan:contacts:customer_id` oder `empty:customers`. */
  id: z.string(),
  category: z.enum(['orphan', 'empty']),
  /** Betroffene Tabelle (technischer Name aus der Allowlist). */
  table: z.string(),
  tableLabel: z.string(),
  title: z.string(),
  description: z.string(),
  count: z.number(),
});
export type DataHealthCheck = z.infer<typeof dataHealthCheckSchema>;

/** Auszuräumende Zeilen-IDs einer Prüfung. */
export const dataHealthPurgeSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
});
export type DataHealthPurgeInput = z.infer<typeof dataHealthPurgeSchema>;

/** Ergebnis eines Purge: gelöscht, übersprungen (passt nicht mehr), fehlgeschlagen. */
export const dataHealthPurgeResultSchema = z.object({
  deleted: z.number(),
  skipped: z.number(),
  failed: z.number(),
});
export type DataHealthPurgeResult = z.infer<typeof dataHealthPurgeResultSchema>;

/**
 * „Tabelle leeren": löscht ALLE Datensätze der Tabelle (tenant-scoped).
 * `confirm` muss exakt dem technischen Tabellennamen entsprechen (Schutz vor
 * versehentlichem Auslösen).
 */
export const dataTruncateSchema = z.object({
  confirm: z.string().min(1),
});
export type DataTruncateInput = z.infer<typeof dataTruncateSchema>;

/** Ergebnis von „Tabelle leeren": Anzahl gelöschter Zeilen. */
export const dataTruncateResultSchema = z.object({ deleted: z.number() });
export type DataTruncateResult = z.infer<typeof dataTruncateResultSchema>;

/**
 * Fester Bestätigungstext für das Leeren MEHRERER Tabellen (Bulk). Anders als
 * beim Einzel-Leeren (dort der Tabellenname) wird hier ein einheitliches Wort
 * getippt — Schutz vor versehentlichem Massenlöschen.
 */
export const DATA_BULK_TRUNCATE_CONFIRM = 'LÖSCHEN';

/** Eintrag im Bulk-Plan/-Ergebnis: Tabelle + (mandantengescoped) Zeilenzahl. */
const dataBulkTableSchema = z.object({ name: z.string(), label: z.string(), count: z.number() });

/** Eingabe für Vorschau/Ausführung des Bulk-Leerens: Liste technischer Tabellennamen. */
export const dataBulkTruncateTablesSchema = z.object({
  tables: z.array(z.string().min(1)).min(1).max(50),
});
export type DataBulkTruncateTablesInput = z.infer<typeof dataBulkTruncateTablesSchema>;

/**
 * Vorschau für „Mehrere Tabellen leeren": ausgewählte Zieltabellen plus alle
 * zusätzlich (über Fremdschlüssel) mitgeleerten Tabellen, jeweils mit Zeilenzahl.
 * `blocked`, falls eine beteiligte Tabelle keine Mandantentrennung hat.
 */
export const dataBulkTruncatePlanSchema = z.object({
  targets: z.array(dataBulkTableSchema),
  cascade: z.array(dataBulkTableSchema),
  /** Summe aller zu löschenden Zeilen (Ziele + Kaskade, mandantengescoped). */
  grandTotal: z.number(),
  blocked: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type DataBulkTruncatePlan = z.infer<typeof dataBulkTruncatePlanSchema>;

/** Ausführung des Bulk-Leerens: Tabellen + Bestätigungswort. */
export const dataBulkTruncateSchema = z.object({
  tables: z.array(z.string().min(1)).min(1).max(50),
  confirm: z.string().min(1),
});
export type DataBulkTruncateInput = z.infer<typeof dataBulkTruncateSchema>;

/** Ergebnis des Bulk-Leerens: Gesamtzahl + gelöschte Zeilen je Zieltabelle. */
export const dataBulkTruncateResultSchema = z.object({
  deleted: z.number(),
  tables: z.array(z.object({ name: z.string(), label: z.string(), deleted: z.number() })),
});
export type DataBulkTruncateResult = z.infer<typeof dataBulkTruncateResultSchema>;

/**
 * Vorschau für „Tabelle leeren": die Zieltabelle und alle abhängigen Tabellen
 * (über Fremdschlüssel), die mitgeleert werden müssen. `blocked` signalisiert,
 * dass nicht sicher (mandantengetrennt) gelöscht werden kann.
 */
export const dataTruncatePlanSchema = z.object({
  table: z.string(),
  label: z.string(),
  /** Zeilen der Zieltabelle (mandantengescoped). */
  total: z.number(),
  /** Abhängige Tabellen (FK), die ebenfalls geleert werden — mit Zeilenzahl. */
  dependents: z.array(z.object({ name: z.string(), label: z.string(), count: z.number() })),
  blocked: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type DataTruncatePlan = z.infer<typeof dataTruncatePlanSchema>;
